import { ImageBlock } from "@circle-react-shared/EditorContentBlocks/ImageBlock";
import { supportsGalleryPreview } from "@circle-react-uikit/TipTap/utilities/contentTypeParser";
import type { Editor } from "@tiptap/react";

import { classNames } from "@/react/helpers/twMergeWithCN";

import { useBlockEditorContext } from "../BlockEditorContext";
import { AlignmentPicker } from "./AlignmentPicker";
import { EmailImageToolbar } from "./EmailImage/EmailImageToolbar";

interface ImageContainerProps {
  attachmentMeta: any;
  deleteNode: () => void;
  editor: Editor;
  node: any;
  onClickOpenAttachmentGallery: () => void;
  updateAttributes: (attrs: any) => void;
}

export const ImageContainer = ({
  node,
  editor,
  attachmentMeta,
  onClickOpenAttachmentGallery,
  updateAttributes,
  deleteNode,
}: ImageContainerProps) => {
  const { isWithinEmailEditor } = useBlockEditorContext() ?? {};
  const isEditable = editor.isEditable;
  const isPreviewable = supportsGalleryPreview(attachmentMeta.content_type);

  const shouldShowNormalToolbar = isEditable && !isWithinEmailEditor;
  /* Older emails would have images in old format. Show these images, but with the email toolbar. */
  const shouldShowEmailToolbar = isEditable && isWithinEmailEditor;

  let widthRatio = 1;
  if (node.attrs.width_ratio) {
    widthRatio = node.attrs.width_ratio;
  } else if (node.attrs.width) {
    // Temporary failsafe in case data is not properly migrated.
    widthRatio = node.attrs.width === "100%" ? 1 : 0.5;
  }

  const width = `${widthRatio * 100}%`;

  const originalWidth = attachmentMeta?.metadata?.width;
  const originalHeight = attachmentMeta?.metadata?.height;
  const aspectRatio =
    originalWidth && originalHeight
      ? `${originalWidth} / ${originalHeight}`
      : undefined;

  const canOpenGallery = isEditable || isPreviewable;

  return (
    <div
      style={{ width }}
      className={classNames("group relative", {
        "mx-auto": node.attrs.alignment === "center",
      })}
    >
      <ImageBlock
        src={attachmentMeta.url}
        imageVariants={attachmentMeta?.image_variants}
        aspectRatio={aspectRatio}
        isZoomable={!isEditable && isPreviewable}
        onClick={canOpenGallery ? onClickOpenAttachmentGallery : undefined}
      />
      {shouldShowNormalToolbar && (
        <AlignmentPicker node={node} updateAttributes={updateAttributes} />
      )}
      {shouldShowEmailToolbar && (
        <EmailImageToolbar
          editor={editor}
          node={node}
          updateAttributes={updateAttributes}
          deleteNode={deleteNode}
        />
      )}
    </div>
  );
};
